import { useState, useEffect } from 'react';
import { getToken } from '../utils/auth';
import Layout from '../components/Layout';

const AnesthesiaManagement = () => {
  const [anesthesiaTypes, setAnesthesiaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [submitting, setSubmitting] = useState(false);

  const API_URL = 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';

  useEffect(() => {
    fetchAnesthesiaTypes();
  }, []);

  const fetchAnesthesiaTypes = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/anesthesia-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch anesthesia types');
      }

      const typesData = await response.json();
      setAnesthesiaTypes(typesData);
    } catch (err) {
      setError('Failed to load anesthesia types');
      console.error('Anesthesia types fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/anesthesia-types`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add anesthesia type');
      }

      // Refresh anesthesia types list
      await fetchAnesthesiaTypes();
      
      // Reset form
      setFormData({ name: '' });
      setShowAddForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (anesthesiaType) => {
    if (!window.confirm(`Are you sure you want to delete "${anesthesiaType.name}"?`)) {
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/anesthesia-types/${anesthesiaType.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete anesthesia type');
      }

      // Refresh anesthesia types list
      await fetchAnesthesiaTypes();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setFormData({ name: '' });
    setShowAddForm(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const defaultTypes = anesthesiaTypes.filter(t => t.type === 'default');
  const customTypes = anesthesiaTypes.filter(t => t.type === 'custom');

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Anesthesia Type Management</h1>
              <p className="text-gray-600 mt-1">Manage anesthesia types and add custom ones for your services</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Add Anesthesia Type
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="p-6">
          {/* Add Form */}
          {showAddForm && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Anesthesia Type</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Anesthesia Type Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter anesthesia type name"
                  />
                  <p className="mt-1 text-sm text-gray-500">{formData.name.length}/100 characters</p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={submitting || !formData.name.trim()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Adding...' : 'Add Anesthesia Type'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Default Anesthesia Types */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Default Anesthesia Types</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {defaultTypes.map((type) => (
                <div key={type.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{type.name}</h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                        Default
                      </span>
                    </div>
                    <div className="text-blue-600">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Anesthesia Types */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Your Custom Anesthesia Types ({customTypes.length})
            </h2>
            {customTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2">No custom anesthesia types yet</p>
                <p className="text-sm">Add your first custom anesthesia type to get started</p>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {customTypes.map((type) => (
                  <div key={type.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{type.name}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                          Custom
                        </span>
                      </div>
                      <button
                        onClick={() => handleDelete(type)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete anesthesia type"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AnesthesiaManagement;